import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Rocket, Sparkles } from "lucide-react";
import { JOURNEY } from "@/constants/testIds";

function compoundSIP(monthly, years, annualRate) {
  // Future value of monthly SIP
  const r = annualRate / 12 / 100;
  const n = years * 12;
  if (r === 0) return monthly * n;
  return monthly * (((Math.pow(1 + r, n) - 1) / r) * (1 + r));
}

function buildSeries(monthly, years, rate) {
  const points = [];
  for (let y = 0; y <= years; y++) {
    points.push({
      year: y,
      label: `Y${y}`,
      Invested: monthly * 12 * y,
      Value: Math.round(compoundSIP(monthly, y, rate)),
    });
  }
  return points;
}

const MILESTONES = [
  { y: 3, t: "Emergency fund built", emoji: "🛡️" },
  { y: 7, t: "Down-payment ready", emoji: "🏡" },
  { y: 12, t: "Kid's education funded", emoji: "🎓" },
  { y: 20, t: "Crore in the bag", emoji: "💎" },
  { y: 30, t: "Retirement secured", emoji: "🌅" },
];

export default function InvestmentJourney() {
  const [monthly, setMonthly] = useState(10000);
  const [years, setYears] = useState(20);
  const rate = 12;

  const data = useMemo(() => buildSeries(monthly, years, rate), [monthly, years]);
  const final = data[data.length - 1];

  return (
    <section
      data-testid={JOURNEY.section}
      id="journey"
      className="relative overflow-hidden py-24 md:py-32"
    >
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-secondary/10 via-background to-background"></div>

      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 mb-10">
          <div className="md:col-span-6">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-secondary font-medium">
              <Sparkles className="w-3.5 h-3.5" /> Gamified Journey
            </span>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-primary mt-3 leading-[1.1]">
              Watch your <em className="not-italic text-secondary">money</em> grow up.
            </h2>
            <p className="mt-5 text-foreground/75 leading-relaxed">
              Move the sliders. Watch the milestones light up as your money compounds. This is the magic
              of small, consistent SIPs — the kind of magic students never get taught in school.
            </p>
          </div>

          <div className="md:col-span-6">
            <div className="bg-card rounded-2xl border border-border p-6 md:p-8 shadow-sm">
              <div className="space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-foreground/80">Monthly SIP</label>
                    <span className="font-serif text-2xl text-primary">₹{monthly.toLocaleString("en-IN")}</span>
                  </div>
                  <input
                    type="range"
                    min={500}
                    max={100000}
                    step={500}
                    value={monthly}
                    onChange={(e) => setMonthly(Number(e.target.value))}
                    data-testid={`${JOURNEY.slider}-amount`}
                    className="w-full accent-secondary"
                  />
                  <div className="flex justify-between text-[11px] text-muted-foreground mt-1">
                    <span>₹500</span><span>₹1,00,000</span>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-foreground/80">Time horizon</label>
                    <span className="font-serif text-2xl text-primary">{years} yrs</span>
                  </div>
                  <input
                    type="range"
                    min={1}
                    max={35}
                    step={1}
                    value={years}
                    onChange={(e) => setYears(Number(e.target.value))}
                    data-testid={`${JOURNEY.slider}-years`}
                    className="w-full accent-secondary"
                  />
                  <div className="flex justify-between text-[11px] text-muted-foreground mt-1">
                    <span>1 yr</span><span>35 yrs</span>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 pt-2">
                  <div className="rounded-xl bg-muted/60 p-3 text-center">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Invested</div>
                    <div className="font-serif text-lg text-foreground mt-1">₹{(final.Invested / 100000).toFixed(1)}L</div>
                  </div>
                  <div className="rounded-xl bg-primary/5 p-3 text-center">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Future Value</div>
                    <div className="font-serif text-lg text-primary mt-1">₹{(final.Value / 100000).toFixed(1)}L</div>
                  </div>
                  <div className="rounded-xl bg-secondary/10 p-3 text-center">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Wealth Gained</div>
                    <div className="font-serif text-lg text-secondary mt-1">
                      ₹{((final.Value - final.Invested) / 100000).toFixed(1)}L
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Chart */}
        <div className="bg-card rounded-3xl border border-border p-4 md:p-8 shadow-sm">
          <div className="h-72 md:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="j-val" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(155 30% 25%)" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="hsl(155 30% 25%)" stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="j-inv" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(24 45% 55%)" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="hsl(24 45% 55%)" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(40 20% 80%)" />
                <XAxis dataKey="label" stroke="hsl(0 0% 45%)" fontSize={12} />
                <YAxis
                  stroke="hsl(0 0% 45%)"
                  fontSize={12}
                  tickFormatter={(v) => v >= 10000000 ? `${(v / 10000000).toFixed(1)}Cr` : `${(v / 100000).toFixed(1)}L`}
                />
                <Tooltip
                  formatter={(v) => `₹${Number(v).toLocaleString("en-IN")}`}
                  contentStyle={{ borderRadius: 12, border: "1px solid hsl(40 20% 85%)" }}
                />
                <Area type="monotone" dataKey="Invested" stroke="hsl(24 45% 55%)" strokeWidth={2} fill="url(#j-inv)" />
                <Area type="monotone" dataKey="Value" stroke="hsl(155 30% 25%)" strokeWidth={2.5} fill="url(#j-val)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Milestones */}
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-5 gap-3">
            {MILESTONES.map((m) => {
              const unlocked = years >= m.y;
              return (
                <motion.div
                  key={m.t}
                  animate={{
                    scale: unlocked ? 1 : 0.96,
                    opacity: unlocked ? 1 : 0.45,
                  }}
                  transition={{ type: "spring", stiffness: 220, damping: 22 }}
                  className={`rounded-2xl p-4 border text-center ${
                    unlocked ? "border-secondary bg-secondary/10" : "border-border bg-muted/30"
                  }`}
                >
                  <div className="text-2xl">{m.emoji}</div>
                  <div className={`mt-1 text-[11px] uppercase tracking-widest ${
                    unlocked ? "text-secondary" : "text-muted-foreground"
                  }`}>Year {m.y}</div>
                  <div className="text-sm mt-1 text-foreground/85 leading-tight">{m.t}</div>
                </motion.div>
              );
            })}
          </div>

          <div className="mt-6 flex items-center gap-2 text-xs text-muted-foreground">
            <Rocket className="w-3.5 h-3.5 text-secondary" />
            Assumes 12% annualised returns. Actual results vary with market and scheme selection.
          </div>
        </div>
      </div>
    </section>
  );
}
